import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Star, Phone, Leaf, Snowflake, CheckCircle, Trees, Hammer, Wind } from 'lucide-react';

const DoorHangerTrueNorth = () => {
  const phoneNumber = "403-919-9057";
  const websiteURL = "https://truenorth-staging.preview.emergentagent.com";
  
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
      {/* Print-ready door hanger - 4.25" x 11" */}
      <div className="relative w-[425px] h-[1100px] bg-white shadow-2xl overflow-hidden">
        
        {/* Door Hanger Hook Cutout */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-16 bg-white">
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-gray-100 rounded-full border-4 border-white"></div>
        </div>

        {/* Main Content */}
        <div className="relative pt-20 px-6 h-full flex flex-col">
          
          {/* Logo Section */}
          <div className="text-center mb-4">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png"
              alt="True North Alberta Property Services"
              className="h-16 w-auto mx-auto mb-2"
            />
          </div>

          {/* Main Message */}
          <div className="text-center mb-4">
            <div className="bg-gradient-to-r from-forest-600 to-forest-700 text-white py-3 px-3 rounded-xl shadow-lg mb-3">
              <h2 className="text-2xl font-black mb-1">LANDSCAPING & SNOW REMOVAL</h2>
              <p className="text-sm font-semibold">Professional • Reliable • Year-Round</p>
            </div>
            <p className="text-lg font-bold text-gray-800">Calgary & Surrounding Areas</p>
          </div>

          {/* Key Benefits */}
          <div className="bg-gray-50 rounded-xl p-3 mb-4 border-2 border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-forest-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-xs text-gray-900">Year-Round</p>
                  <p className="text-xs text-gray-600">All Seasons</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-forest-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-xs text-gray-900">Local Business</p>
                  <p className="text-xs text-gray-600">Alberta Owned</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-forest-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-xs text-gray-900">Free Quotes</p>
                  <p className="text-xs text-gray-600">No Obligation</p>
                </div>
              </div>
            </div>
          </div>

          {/* Services Offered */}
          <div className="mb-3">
            <h3 className="text-center text-sm font-bold text-gray-900 mb-2 bg-forest-50 py-2 rounded-lg">Our Services</h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Leaf className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Lawn Care & Mowing</p>
              </div>
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Leaf className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Garden & Bed Maintenance</p>
              </div>
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Trees className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Tree & Shrub Pruning</p>
              </div>
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Wind className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Spring & Fall Clean-Ups</p>
              </div>
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Hammer className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Patios, Walkways & Hardscaping</p>
              </div>
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200">
                <Snowflake className="w-4 h-4 text-forest-600 flex-shrink-0" />
                <p className="font-semibold text-gray-900">Snow Removal & Winter Pruning</p>
              </div>
            </div>
          </div>

          {/* 5 Star Reviews */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-2 mb-3">
            <div className="flex items-center justify-center gap-1 mb-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-center text-xs font-bold text-gray-900">5-STAR RATED SERVICE</p>
            <p className="text-center text-xs text-gray-700 italic">"Professional and reliable!"</p>
          </div>

          {/* Phone Number - Primary CTA */}
          <div className="bg-forest-600 text-white rounded-xl p-3 mb-4 text-center">
            <p className="text-xs font-semibold mb-1 tracking-wide">CALL FOR A FREE QUOTE</p>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Phone className="w-5 h-5" />
              <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="text-2xl font-black tracking-tight">
                {phoneNumber}
              </a>
            </div>
            <p className="text-xs text-forest-100">Available Year-Round</p>
          </div>

          {/* QR Code */}
          <div className="bg-white rounded-xl p-4 text-center mb-6">
            <p className="text-sm font-bold text-gray-900 mb-3">SCAN FOR MORE INFO</p>
            <div className="flex justify-center mb-2">
              <QRCodeSVG 
                value={websiteURL}
                size={110}
                level="H"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-gray-600 font-medium">Visit Our Website</p>
          </div>

          {/* Bottom Tagline */}
          <div className="text-center py-2 border-t-2 border-gray-200 mb-2">
            <p className="text-xs font-bold text-forest-600 uppercase tracking-wider">Locally Owned • Professional Service</p>
          </div>
        </div>
      </div>

      {/* Print Button (only shows on screen, not in print) */}
      <button 
        onClick={() => window.print()} 
        className="fixed bottom-8 right-8 bg-forest-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-forest-700 transition-colors print:hidden font-semibold"
      >
        Print Door Hanger
      </button>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: 4.25in 11in;
            margin: 0;
          }
          .bg-gray-100 {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DoorHangerTrueNorth;
